<div class="filter-rows-container">
  @for (filterRow of filterRows; track trackByIndex($index, filterRow); let i = $index) {
    <div class="filter-row">
      <ul class="filters">
        <li class="field-input-container">
          <label>Field</label>
          <app-kp-autocomplete
            [selected]="getSelectedFieldName(filterRow)"
            [filterFunction]="this.entityFieldsFilterFn.bind(this)"
            [placeholder]="'Search...'"
            [position]="'fixed'"
            [minWidth]="'200px'"
            [valid]="filterRow.formGroup.get('field')?.valid"
            [matTooltip]="'Search for a field'"
            [matTooltipPosition]="'above'"
            [matTooltipShowDelay]="500"
            (selectedChange)="onFieldSelected($event, filterRow)"
            (inputCleared)="onFieldInputCleared(filterRow)"
          >
          </app-kp-autocomplete>
        </li>
        <li>
          <label>Operator</label>
          <mat-select
            [value]="getSelectedOperator(filterRow)"
            (selectionChange)="onOperatorSelected($event, filterRow)"
            placeholder="Select an operator..."
            [matTooltip]="'Select an operator'"
            [matTooltipPosition]="'above'"
            [matTooltipShowDelay]="500"
          >
            @for (option of operatorOptions; track option) {
              <mat-option [value]="option">
                {{ option.name }}
              </mat-option>
            }
          </mat-select>
        </li>
        <li [class.disabled]="!hasFieldSelected(filterRow)" class="value-input-container">
          <label>Value</label>

          @switch (getFieldType(filterRow)) {
            <!-- Text input for string fields -->
            @case ('text') {
              <!-- Single value input for equals/not-equals -->
              @if (!isMultiValueOperator(filterRow)) {
                <input
                  [formControl]="filterRow.formGroup.get('value')"
                  [placeholder]="getInputPlaceholder(filterRow)"
                  [maxlength]="getInputMaxLength(filterRow)"
                  [matTooltip]="getInputPlaceholder(filterRow)"
                  [matTooltipPosition]="'above'"
                  [matTooltipShowDelay]="500"
                  (input)="onValueChanged(filterRow)"
                />
              }

              <!-- Multi-value autocomplete for includes/not-includes -->
              @if (isMultiValueOperator(filterRow)) {
                <div class="multi-value-autocomplete" [class.invalid-picker]="filterRow.formGroup.get('value')?.invalid">
                  <input
                    type="text"
                    [placeholder]="getMultiValueInputPlaceholder(filterRow)"
                    [maxlength]="getInputMaxLength(filterRow)"
                    matInput
                    [formControl]="getMultiValueFormControl(filterRow)"
                    (keydown.enter)="addMultiValueFromInput(filterRow); $event.preventDefault()"
                    [matAutocomplete]="textAuto"
                    [matTooltip]="'Search or add your own...'"
                    [matTooltipPosition]="'above'"
                    [matTooltipShowDelay]="500"
                    #textInput
                  />
                  <span class="clear-input">
                    <span
                      class="return-icon"
                      [ngClass]="{ green: isAddingValue(filterRow), disabled: !textInput.value }"
                      (click)="addMultiValueFromInput(filterRow)"
                      (keydown.enter)="addMultiValueFromInput(filterRow); $event.preventDefault()"
                      (keydown.space)="addMultiValueFromInput(filterRow); $event.preventDefault()"
                      [tabindex]="textInput.value ? '0' : '-1'"
                      matTooltip="Hit 'Enter' to add"
                      matTooltipShowDelay="500"
                    ></span>
                    <mat-icon
                      fontIcon="close"
                      class="clear-icon remove"
                      [ngClass]="{ disabled: !textInput.value }"
                      (click)="clearMultiValueInput(filterRow)"
                      (keydown.enter)="clearMultiValueInput(filterRow); $event.preventDefault()"
                      (keydown.space)="clearMultiValueInput(filterRow); $event.preventDefault()"
                      [tabindex]="textInput.value ? '0' : '-1'"
                      [matTooltip]="textInput.value ? 'Clear' : ''"
                      matTooltipShowDelay="500"
                    ></mat-icon>
                  </span>

                  <mat-autocomplete #textAuto="matAutocomplete">
                    @for (value of getMultiValues(filterRow); track value; let i = $index) {
                      <mat-option [value]="value">
                        <span class="stay-open" (click)="stayOpen($event)">
                          <span>{{ value }}</span>
                          <mat-icon
                            fontIcon="close"
                            class="remove-icon"
                            tabindex="0"
                            matTooltip="Remove this value"
                            matTooltipShowDelay="500"
                            (click)="removeMultiValue(filterRow, i, $event)"
                            (keydown.enter)="removeMultiValue(filterRow, i, $event); $event.preventDefault()"
                            (keydown.space)="removeMultiValue(filterRow, i, $event); $event.preventDefault()"
                          ></mat-icon>
                        </span>
                      </mat-option>
                    }
                  </mat-autocomplete>
                </div>
              }
            }

            <!-- Number input for integer/decimal fields -->
            @case ('number') {
              <!-- Single value input for equals/not-equals -->
              @if (!isMultiValueOperator(filterRow)) {
                <input
                  type="number"
                  [formControl]="filterRow.formGroup.get('value')"
                  [placeholder]="getInputPlaceholder(filterRow)"
                  [matTooltip]="getInputPlaceholder(filterRow)"
                  [matTooltipPosition]="'above'"
                  [matTooltipShowDelay]="500"
                  (input)="onValueChanged(filterRow)"
                />
              }

              <!-- Multi-value autocomplete for includes/not-includes -->
              @if (isMultiValueOperator(filterRow)) {
                <div class="multi-value-autocomplete" [class.invalid-picker]="filterRow.formGroup.get('value')?.invalid">
                  <input
                    type="number"
                    [placeholder]="getMultiValueInputPlaceholder(filterRow)"
                    matInput
                    [formControl]="getMultiValueFormControl(filterRow)"
                    (keydown.enter)="addMultiValueFromInput(filterRow); $event.preventDefault()"
                    [matAutocomplete]="numberAuto"
                    #numberInput
                    [matTooltip]="'Search or add your own...'"
                    [matTooltipPosition]="'above'"
                    [matTooltipShowDelay]="500"
                  />
                  <span class="clear-input">
                    <span
                      class="return-icon"
                      [ngClass]="{ green: isAddingValue(filterRow), disabled: !numberInput.value }"
                      (click)="addMultiValueFromInput(filterRow)"
                      (keydown.enter)="addMultiValueFromInput(filterRow); $event.preventDefault()"
                      (keydown.space)="addMultiValueFromInput(filterRow); $event.preventDefault()"
                      [tabindex]="numberInput.value ? '0' : '-1'"
                      matTooltip="Hit 'Enter' to add"
                      matTooltipShowDelay="500"
                    ></span>
                    <mat-icon
                      fontIcon="close"
                      class="clear-icon remove"
                      [ngClass]="{ disabled: !numberInput.value }"
                      (click)="clearMultiValueInput(filterRow)"
                      (keydown.enter)="clearMultiValueInput(filterRow); $event.preventDefault()"
                      (keydown.space)="clearMultiValueInput(filterRow); $event.preventDefault()"
                      [tabindex]="numberInput.value ? '0' : '-1'"
                      [matTooltip]="numberInput.value ? 'Clear' : ''"
                      matTooltipShowDelay="500"
                    ></mat-icon>
                  </span>

                  <mat-autocomplete #numberAuto="matAutocomplete">
                    @for (value of getMultiValues(filterRow); track value; let i = $index) {
                      <mat-option [value]="value">
                        <span class="stay-open" (click)="stayOpen($event)">
                          <span class="option-item">{{ value }}</span>
                          <mat-icon
                            fontIcon="close"
                            class="remove-icon"
                            tabindex="0"
                            matTooltip="Remove this value"
                            matTooltipShowDelay="500"
                            (click)="removeMultiValue(filterRow, i, $event)"
                            (keydown.enter)="removeMultiValue(filterRow, i, $event); $event.preventDefault()"
                            (keydown.space)="removeMultiValue(filterRow, i, $event); $event.preventDefault()"
                          ></mat-icon>
                        </span>
                      </mat-option>
                    }
                  </mat-autocomplete>
                </div>
              }
            }

            <!-- Select dropdown for fields with predefined options -->
            @case ('select') {
              <!-- Single select for equals/not-equals -->
              @if (!isMultiValueOperator(filterRow)) {
                <mat-select
                  [formControl]="filterRow.formGroup.get('value')"
                  placeholder="Select value"
                  [matTooltip]="'Select value'"
                  [matTooltipPosition]="'above'"
                  [matTooltipShowDelay]="500"
                  (selectionChange)="onValueChanged(filterRow)"
                >
                  <mat-select-trigger>
                    @if (filterRow.formGroup.get('value')?.value) {
                      {{
                        getCurrentFieldOptionLabel(filterRow).length > 70
                          ? (getCurrentFieldOptionLabel(filterRow) | slice: 0 : 70) + '...'
                          : getCurrentFieldOptionLabel(filterRow)
                      }}
                    }
                  </mat-select-trigger>
                  @for (option of getFieldOptions(filterRow); track option.value) {
                    <mat-option [value]="option.value">
                      {{ option.label }}
                    </mat-option>
                  }
                </mat-select>
              }

              <!-- Multi-select for includes/not-includes -->
              @if (isMultiValueOperator(filterRow)) {
                <mat-select
                  multiple
                  [formControl]="filterRow.formGroup.get('value')"
                  placeholder="Select values"
                  [matTooltip]="'Select values'"
                  [matTooltipPosition]="'above'"
                  [matTooltipShowDelay]="500"
                  (selectionChange)="onMultiSelectChanged(filterRow)"
                >
                  <mat-select-trigger>
                    @if (filterRow.formGroup.get('value')?.value?.length) {
                      {{
                        getFieldOptionLabel(filterRow, filterRow.formGroup.get('value')?.value[0]).length > 70
                          ? (getFieldOptionLabel(filterRow, filterRow.formGroup.get('value')?.value[0]) | slice: 0 : 70) + '...'
                          : getFieldOptionLabel(filterRow, filterRow.formGroup.get('value')?.value[0])
                      }}
                    }
                    @if (filterRow.formGroup.get('value')?.value?.length > 1) {
                      <span> (+{{ filterRow.formGroup.get('value')?.value?.length - 1 }} more) </span>
                    }
                  </mat-select-trigger>
                  @for (option of getFieldOptionsWithSelectedFirst(filterRow); track option.value) {
                    <mat-option [value]="option.value">
                      {{ option.label }}
                    </mat-option>
                  }
                </mat-select>
              }
            }

            <!-- Relationship fields -->
            @case ('relationship') {
              <app-multi-select-autocomplete
                [key]="getSelectedField(filterRow).id"
                [allowMultiple]="isOperatorMultiValue(filterRow)"
                [queryFunction]="service.getQueryFunctionForField(getSelectedField(filterRow).id).bind(this)"
                [initialSelectedValues]="getRelationshipInitialValues(filterRow)"
                [matTooltip]="'Search and select...'"
                [matTooltipPosition]="'above'"
                [matTooltipShowDelay]="500"
                [valid]="filterRow.formGroup.get('value')?.valid"
                (selectionChange)="onRelationshipSelectionChange($event, filterRow)"
              ></app-multi-select-autocomplete>
            }

            <!-- Disabled state when no field is selected -->
            @default {
              <input placeholder="Select a field first..." [disabled]="true" />
            }
          }
        </li>
        <li class="row-actions">
          <button
            type="button"
            class="remove-row-btn"
            (click)="removeFilterRow(i)"
            matTooltip="Remove restriction"
            matTooltipShowDelay="500"
          >
            <mat-icon fontIcon="close"></mat-icon>
          </button>
        </li>
      </ul>
    </div>
  }

  <div class="add-filter-container">
    <button
      [disabled]="loading"
      theme="small"
      class="add-filter-btn"
      type="button"
      (click)="addFilterRow()"
      matTooltip="Add new restriction"
      matTooltipShowDelay="500"
    >
      Add Restriction
      <mat-icon fontIcon="add" class="add-icon"></mat-icon>
    </button>
  </div>
</div>
